import { ROUTES } from '@/app/config/constants';

/**
 * Where a switch of entity lands a user who is on a template's or a record's screen (bug, 2026-10-29).
 *
 * Templates and records belong to an entity. `Layout` remounts the screen on a switch but leaves the address alone, and
 * an address that names the previous entity's template or record asked for it again under the new entity, which the
 * platform refused: "This template is mapped to entities you are not assigned to".
 *
 * - **A create from a template** -- Add Supplier's `/masters/supplier/new/:templateId`, Create BID's
 *   `/rfqs/new?templateId=`, and a Supplier or Purchase Request duplicate -- goes back to that kind's template picker,
 *   which lists the new entity's templates (or says it has none). New Purchase Request keeps its template in the page
 *   and its picker on the same address, so the remount alone starts it again.
 * - **A record's View or Edit** -- a Supplier, a Purchase Request, a BID (a duplicated BID is a saved draft, opened
 *   for Edit) -- goes to that module's list, which lists the new entity's.
 *
 * Null for every other screen: the address is kept, as before.
 */
export function landingAfterEntitySwitch(pathname: string): string | null {
  const path = pathname.replace(/\/+$/, '');
  const supplier = ROUTES.MASTERS_SUPPLIER;
  const requests = ROUTES.PURCHASE_REQUESTS;
  if (path.startsWith(`${supplier}/new/`) || isRecord(path, supplier, 'duplicate')) return `${supplier}/new`;
  if (isRecord(path, supplier) || isRecord(path, supplier, 'edit')) return supplier;
  if (isRecord(path, requests, 'duplicate')) return `${requests}/new`;
  if (isRecord(path, requests) || isRecord(path, requests, 'edit')) return requests;
  if (path === ROUTES.RFQS_NEW) return ROUTES.RFQS_TEMPLATES;
  if (isRecord(path, ROUTES.RFQS) || isRecord(path, ROUTES.RFQS, 'edit')) return ROUTES.RFQS;
  return null;
}

/** The pages under a module's list that are not a record's own: its picker and Create. */
const NOT_RECORDS: readonly string[] = ['new', 'templates'];

/** Whether `path` is `<base>/<id>`, or `<base>/<id>/<page>` when a page is given. */
function isRecord(path: string, base: string, page?: string): boolean {
  const rest = path.startsWith(`${base}/`) ? path.slice(base.length + 1).split('/') : [];
  const [id, ...tail] = rest;
  if (id === undefined || id === '' || NOT_RECORDS.includes(id)) return false;
  return page === undefined ? tail.length === 0 : tail.length === 1 && tail[0] === page;
}
